import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box, InputGroup, Input, InputRightElement, IconButton, Flex, Text, Link, CloseButton,
  useDisclosure, Image, Tabs, TabList, Tab, TabPanels, TabPanel
} from '@chakra-ui/react';
import { Search2Icon } from '@chakra-ui/icons';
import { motion } from 'framer-motion';
import axiosInstance from '../../services/axios';

const MotionBox = motion(Box);

const SearchBar = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState({});
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [activeTab, setActiveTab] = useState(0); // Track active tab for filtering
  const [isLoading, setIsLoading] = useState(false);
  const searchBoxRef = useRef(); // Reference to detect clicks outside
  const debounceTimerRef = useRef(null);

  const handleClose = useCallback(() => {
    setSearchTerm('');
    setResults({});
    setIsLoading(false);
    onClose();
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
  }, [onClose]);

  // Debounced search function
  const debouncedSearch = useCallback((term) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(async () => {
      if (term.trim()) {
        setIsLoading(true);
        try {
          onOpen();
          const response = await axiosInstance.post(`/music/atlas/search?request=${term}`);
          setResults(response.data);
        } catch (error) {
          console.error('Search error:', error);
          setResults({});
        } finally {
          setIsLoading(false);
        }
      }
    }, 300); // 300ms debounce delay
  }, [onOpen]);

  // Close results when clicking outside the search box
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) {
        handleClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClose]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.trim()) {
      debouncedSearch(value);
    } else {
      handleClose();
    }
  }, [debouncedSearch, handleClose]);

  const handleSearch = useCallback(async (term) => {
    if (term.trim()) {
      setIsLoading(true);
      try {
        onOpen();
        const response = await axiosInstance.post(`/music/atlas/search?request=${term}`);
        setResults(response.data);
      } catch (error) {
        console.error('Search error:', error);
        setResults({});
      } finally {
        setIsLoading(false);
      }
    }
  }, [onOpen]);

  return (
    <Box ref={searchBoxRef} w={{ base: "100%", md: "50vw", lg: "25vw" }}>
      <Box top={0} zIndex={10}>
        <InputGroup>
          <Input
            value={searchTerm}
            onChange={handleInputChange}
            placeholder="Search..."
            variant="filled"
            bg="secondary.100"
            boxShadow="dark-lg"
            _focus={{ borderColor: "dark.900" }}
          />
          <InputRightElement>
            <IconButton
              aria-label="Search database"
              icon={<Search2Icon />}
              onClick={() => handleSearch(searchTerm)}
              variant="ghost"
              color="dark.900"
            />
          </InputRightElement>
        </InputGroup>
      </Box>
      {isOpen && (
        <MotionBox
          bg="secondary.500"
          p={3}
          mt={2}
          position="absolute"
          w={{ base: "85%", md: "50vw", lg: "25vw" }}
          borderRadius="md"
          boxShadow="dark-lg"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
          maxHeight="300px"
          overflowY="auto"
        >
          <Flex justify="flex-end" px={3}>
            <CloseButton onClick={handleClose} color="dark.100" bg="dark.900" />
          </Flex>

          <Tabs index={activeTab} onChange={(index) => setActiveTab(index)}>
            <TabList>
              <Tab>Tracks</Tab>
              <Tab>Artists</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                {isLoading ? (
                  <Text>Searching...</Text>
                ) : (
                  <Text>{results.music_results && results.music_results.length === 0 && "No tracks found"}</Text>
                )}
                {results.music_results && results.music_results.map(result => (
                  <Flex
                    key={result.track_id}
                    flexDirection="column"
                    p={4}
                    borderWidth="1px"
                    borderRadius="md"
                    borderColor="dark.900"
                    color="primary.900"
                    m={3}
                    alignItems="flex-start"
                    justifyContent="space-between"
                    bg="secondary.100"
                    _hover={{ bg: "dark.100", color: "dark.900" }}
                  >
                    <Link href={`/track/${result.track_id}`} _hover={{ border: "none", color: "dark.900" }} display="grid" sx={{
                      gridTemplateRows: "1fr 1fr",
                      gridTemplateColumns: "1fr 4fr",
                      alignItems: "center",
                      gap: 4,
                    }}>
                      <Image gridRowStart={1} gridRowEnd={3} gridColumnStart={1} gridColumnEnd={2} src={result.image_url} alt={result.track_name} borderRadius='lg' width={{ base: "3rem", md: "4rem" }} />
                      <Text noOfLines={1} gridRowStart={1} gridRowEnd={2} gridColumnStart={2} gridColumnEnd={3} fontSize="lg" fontWeight="bold" mb={-3}>
                        {result.track_name}
                      </Text>
                      <Text noOfLines={1} mt={-2} gridRowStart={2} gridRowEnd={3} gridColumnStart={2} gridColumnEnd={3} fontSize="sm">
                        {result.artist_name}
                      </Text>
                    </Link>
                    <Text mt={4} justifySelf="flex-start" color="secondary.900">
                      ${result.price_per_share}
                    </Text>
                  </Flex>
                ))}
              </TabPanel>
              <TabPanel>
                {isLoading ? (
                  <Text>Searching...</Text>
                ) : (
                  <Text>{results.artist_results && results.artist_results.length === 0 && "No artists found"}</Text>
                )}
                {results.artist_results && results.artist_results.map(result => (
                  <Flex
                    key={result.account.profile.artist_id[0].id}
                    p={4}
                    borderWidth="1px"
                    borderRadius="md"
                    borderColor="dark.900"
                    color="primary.900"
                    m={3}
                    alignItems="center"
                    justifyContent="space-between"
                    bg="secondary.100"
                    _hover={{ bg: "dark.100", color: "dark.900" }}
                  >
                    <Link href={`/artist/${result.user_id}`} _hover={{ border: "none", color: "dark.900" }} display="grid" sx={{
                      gridTemplateRows: "1fr 1fr",
                      gridTemplateColumns: "1fr 2fr",
                      alignItems: "center",
                      columnGap: 4,
                    }}>
                      <Image gridRowStart={1} gridRowEnd={3} gridColumnStart={1} gridColumnEnd={2} src={result.account.profile.image_url} alt={result.account.profile.artist_name} borderRadius='lg' width={{ base: "3rem", md: "4rem" }} />
                      <Text noOfLines={1} gridRowStart={1} gridRowEnd={2} gridColumnStart={2} gridColumnEnd={3} fontSize="lg" fontWeight="bold" mb={-3}>
                        {result.account.profile.artist_name}
                      </Text>
                    </Link>
                  </Flex>
                ))}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </MotionBox>
      )}
    </Box>
  );
};

export default SearchBar;
